<template>
  <div class="governance-page">
    <header>
      <button @click="$router.back()">
        <van-icon name="arrow-left"/>
      </button>
      <h1>公司治理与AI安全阀</h1></header>
    <section class="page-intro">
      <div class="intro-icon"><van-icon name="shield-o"/></div>
      <div><strong>Boss治理总控</strong><small>先维护公司规则，再设置AI边界。AI只能建议或按规则执行，所有变更均可追溯。</small></div>
    </section>
    <van-tabs v-model="tab" color="#c9232c" sticky>
      <van-tab title="治理版本">
        <section class="toolbar">
          <button @click="showEditor=true">
            <van-icon name="plus"/>
            新增治理规则
          </button>
          <p>战略、政策、岗位职责和成果口径，从这里统一维护和发布。</p></section>
        <van-pull-refresh v-model="loading" @refresh="loadPolicies">
          <article v-for="item in policies" :key="item.id" class="item">
            <div class="item-head"><span>{{ typeName(value(item, 'policy_type')) }}</span><em
              :class="value(item,'status').toLowerCase()">{{ statusName(value(item, 'status')) }}</em></div>
            <div class="item-title">{{ value(item, 'title') }}</div>
            <p>{{ value(item, 'policy_code') }} · V{{ value(item, 'version') }}</p>
            <pre>{{ pretty(value(item, 'content_json')) }}</pre>
            <button v-if="value(item,'status')==='DRAFT'" class="primary" @click="publish(value(item,'id'))">审核并发布</button>
          </article>
          <van-empty v-if="!policies.length" description="暂无治理版本"/>
        </van-pull-refresh>
      </van-tab>
      <van-tab :title="`反馈安全阀${pendingCount?' '+pendingCount:''}`">
        <van-pull-refresh v-model="feedbackLoading" @refresh="loadFeedback">
          <article v-for="item in feedback" :key="value(item,'id')" class="item">
            <div class="item-head"><span>{{ feedbackName(value(item, 'feedback_type')) }}</span><em
              :class="value(item,'status').toLowerCase()">{{ statusName(value(item, 'status')) }}</em></div>
            <div class="item-title">{{ value(item, 'title') }}</div>
            <p>来源：{{ value(item, 'source_type') }} #{{ value(item, 'source_id') }}</p>
            <pre>{{ pretty(value(item, 'proposal_json')) }}</pre>
            <div v-if="value(item,'status')==='PENDING'" class="decisions">
              <button @click="review(value(item,'id'),'REJECT')">驳回</button>
              <button class="primary" @click="review(value(item,'id'),'APPLY')">批准回流</button>
            </div>
          </article>
          <van-empty v-if="!feedback.length" description="暂无闭环反馈"/>
        </van-pull-refresh>
      </van-tab>
    </van-tabs>
    <section class="controls">
      <div class="controls-head">
        <div>
          <div class="section-heading">AI安全阀</div>
          <p class="hint">控制AI是“只建议”还是“审核后执行”，不会修改历史数据。</p></div>
        <button @click="loadControls">
          <van-icon name="replay"/>
          刷新
        </button>
      </div>
      <article v-for="item in controls" :key="value(item,'control_code')" class="control-item">
        <div><strong>{{ controlName(value(item, 'control_code')) }}</strong><small>{{ value(item, 'control_code') }}</small></div>
        <select :value="value(item,'autonomy_level')" @change="changeControl(item,$event.target.value)">
          <option value="DISABLED">停用/冻结</option>
          <option value="SUGGEST_ONLY">仅建议</option>
          <option value="REVIEW_REQUIRED">人工审核后执行</option>
          <option value="AUTO_LOW_RISK">低风险自动执行</option>
        </select>
      </article>
      <van-empty v-if="!controls.length" description="暂无安全阀配置，可先新增治理规则"/>
    </section>
    <van-popup v-model="showEditor" position="bottom" round class="editor">
      <div class="editor-title">新增治理规则</div>
      <label>治理类型
        <van-field readonly clickable :value="typeName(form.policyType)" @click="showTypes=true" right-icon="arrow"/>
      </label>
      <label>规则编码
        <van-field v-model.trim="form.policyCode" placeholder="例如 COMPANY_STRATEGY_2026"/>
      </label>
      <label>标题
        <van-field v-model.trim="form.title" placeholder="老板确认的规则标题"/>
      </label>
      <label>规则内容
        <van-field v-model="form.contentText" type="textarea" rows="6" placeholder="填写战略、政策、职责、纠偏或成果口径"/>
      </label>
      <button class="save" @click="save">保存草稿</button>
    </van-popup>
    <van-action-sheet v-model="showTypes" :actions="types" @select="selectType"/>
  </div>
</template>
<script>
import coreapi from '@/utils/coreapi';

export default {
  data() {
    return {
      tab: 0,
      loading: false,
      feedbackLoading: false,
      controls: [],
      policies: [],
      feedback: [],
      showEditor: false,
      showTypes: false,
      form: {policyType: 'STRATEGY', policyCode: '', title: '', contentText: ''},
      types: [
        {name: '公司战略', value: 'STRATEGY'}, {name: '公司政策', value: 'COMPANY_POLICY'}, {
          name: '岗位职责',
          value: 'POSITION_DUTY'
        }, {name: '纠偏规则', value: 'CORRECTION_RULE'}, {name: '成果考核口径', value: 'OUTCOME_STANDARD'}, {
          name: '品牌方向',
          value: 'BRAND_DIRECTION'
        }, {name: 'AI安全规则', value: 'AI_SAFETY'}]
    };
  },
  computed: {
    pendingCount() {
      return this.feedback.filter(x => this.value(x, 'status') === 'PENDING').length;
    }
  }, mounted() {
    this.loadPolicies();
    this.loadFeedback();
    this.loadControls();
  }, methods: {
    value(row, key) {
      return row[key] !== undefined ? row[key] : row[key.toUpperCase()];
    }, pretty(v) {
      if (!v) return '';
      try {
        return JSON.stringify(typeof v === 'string' ? JSON.parse(v) : v, null, 2)
      } catch (e) {
        return String(v)
      }
    },
    controlName(v) {
      return ({
        BRAND_PUBLISH: '品牌内容发布',
        INTELLIGENCE_TO_TASK: '情报转任务',
        STRATEGY_ADVICE: '战略建议自动执行',
        WORK_POINT_GENERATION: '工作要点生成',
        SKILL_PATCH: 'Skill补丁发布'
      })[v] || '治理场景'
    },
    typeName(v) {
      const x = this.types.find(i => i.value === v);
      return x ? x.name : v;
    }, feedbackName(v) {
      return ({CORRECTION_RESULT: '纠偏验证结果', STRATEGY_OUTCOME: '战略行动成果', BRAND_SAMPLE: '品牌内容样本'})[v] || v;
    }, statusName(v) {
      return ({DRAFT: '草稿', ACTIVE: '已生效', RETIRED: '已替代', PENDING: '待Boss审核', APPLIED: '已批准回流', REJECTED: '已驳回'})[v] || v;
    },
    async loadPolicies() {
      this.loading = true;
      try {
        this.policies = await coreapi.governancePolicies({})
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.loading = false
      }
    }, async loadFeedback() {
      this.feedbackLoading = true;
      try {
        this.feedback = await coreapi.governanceFeedback({})
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.feedbackLoading = false
      }
    }, async loadControls() {
      try {
        this.controls = await coreapi.governanceControls()
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }, async changeControl(item, level) {
      try {
        await coreapi.updateGovernanceControl(this.value(item, 'control_code'), {autonomyLevel: level, enabled: level !== 'DISABLED'});
        await this.loadControls();
        this.$toast.success('安全阀已更新')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    },
    selectType(x) {
      this.form.policyType = x.value;
      this.showTypes = false;
    }, async save() {
      if (!this.form.policyCode || !this.form.title || !this.form.contentText) return this.$toast('请填写完整');
      try {
        await coreapi.createGovernancePolicy({
          policyType: this.form.policyType,
          policyCode: this.form.policyCode,
          title: this.form.title,
          content: {text: this.form.contentText}
        });
        this.showEditor = false;
        this.form = {policyType: 'STRATEGY', policyCode: '', title: '', contentText: ''};
        await this.loadPolicies();
        this.$toast.success('草稿已保存')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    },
    async publish(id) {
      try {
        await coreapi.publishGovernancePolicy(id);
        await this.loadPolicies();
        this.$toast.success('治理版本已生效')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }, async review(id, decision) {
      try {
        await coreapi.reviewGovernanceFeedback(id, {decision, note: decision === 'APPLY' ? 'Boss批准进入后续智能体上下文' : 'Boss驳回，不得回流'});
        await this.loadFeedback();
        this.$toast.success(decision === 'APPLY' ? '已批准回流' : '已驳回')
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }
  }
}</script>
<style lang="less" scoped>
.governance-page {
  min-height: 100vh;
  background: #f5f6f9;
  color: #252832;
  padding-bottom: 30px;
  font-size: 14px
}

header {
  height: 74px;
  background: linear-gradient(100deg, #341b1d, #c6262d);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative
}

header button {
  position: absolute;
  left: 16px;
  border: 0;
  background: none;
  color: #fff;
  font-size: 24px
}

header h1 {
  font-size: 20px;
  letter-spacing: 0
}

.page-intro {
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 12px;
  padding: 14px 16px;
  background: #fff8f8;
  border: 1px solid #f1d9da;
  border-radius: 8px
}

.intro-icon {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #c9232c;
  color: #fff;
  font-size: 20px
}

.page-intro .van-icon {
  font-size: 20px;
}

.page-intro strong, .page-intro small {
  display: block
}

.page-intro strong {
  font-size: 16px
}

.page-intro small {
  margin-top: 4px;
  color: #818796;
  font-size: 12px;
  line-height: 1.5
}

.toolbar {
  padding: 12px
}

.toolbar button, .save {
  width: 100%;
  height: 46px;
  border: 0;
  background: #c9232c;
  color: #fff;
  font-size: 15px;
  border-radius: 8px
}

.toolbar p {
  margin: 8px 2px 0;
  color: #818796;
  font-size: 12px
}

.item {
  margin: 10px 12px;
  padding: 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 16px rgba(30, 34, 45, .05)
}

.item-head {
  display: flex;
  justify-content: space-between;
  align-items: center
}

.item-head span {
  color: #c9232c;
  font-size: 13px
}

.item-head em {
  font-style: normal;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 3px;
  background: #f2f3f5
}

.item-head em.active, .item-head em.applied {
  color: #16835b;
  background: #e8f7f0
}

.item-head em.pending, .item-head em.draft {
  color: #b46a00;
  background: #fff4df
}

.item-title {
  font-size: 16px;
  font-weight: 600;
  margin: 10px 0 6px
}

.item p {
  font-size: 13px;
  color: #818796
}

.item pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f7f8fa;
  padding: 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  max-height: 180px;
  overflow: auto
}

.primary {
  background: #c9232c !important;
  color: #fff !important
}

.item > button {
  border: 0;
  padding: 9px 18px
}

.decisions {
  display: grid;
  grid-template-columns:1fr 1fr;
  gap: 10px
}

.decisions button {
  height: 42px;
  border: 1px solid #c9232c;
  background: #fff;
  color: #c9232c
}

.editor {
  padding: 20px 18px 30px
}

.editor-title {
  font-size: 19px;
  font-weight: 600
}

.editor label {
  display: block;
  margin-top: 14px;
  font-size: 14px;
  color: #555
}

.save {
  margin-top: 20px
}

.controls {
  margin: 12px;
  padding: 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 16px rgba(30, 34, 45, .05)
}

.controls-head {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.section-heading {
  margin: 0;
  font-size: 18px;
  font-weight: 600
}

.controls-head button {
  border: 0;
  background: #f5f6f9;
  color: #c9232c;
  padding: 7px 12px
}

.hint {
  font-size: 12px;
  color: #818796;
  line-height: 1.5;
  margin: 5px 0 0
}

.control-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid #f0f1f3
}

.control-item strong {
  display: block;
  font-size: 14px
}

.control-item small {
  display: block;
  color: #9aa0aa;
  margin-top: 3px
}

.control-item select {
  width: 150px;
  max-width: 48%;
  height: 34px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  padding: 0 6px;
  color: #333
}

/* 与 Boss 端其他页面统一：使用 rem 设计稿、浅灰背景和红色主视觉 */
.governance-page {
  min-height: 100vh;
  padding-bottom: 1.7rem;
  background: #f6f7fb;
}
.governance-page > header {
  height: 1.22667rem;
  margin: .4rem .25rem 0;
  border-radius: .2rem .2rem 0 0;
}
.governance-page > header button {
  left: .18rem;
  font-size: .4rem;
}
.governance-page > header h1 { font-size: .36rem; }
.page-intro, .controls {
  margin: .22rem .28rem;
  padding: .24rem .28rem;
  border-radius: .2rem;
  box-shadow: 0 .06rem .24rem rgba(211,47,47,.08);
}
.page-intro { gap: .14rem; }
.intro-icon { width: .68rem; height: .68rem; flex: 0 0 .68rem; font-size: .36rem; }
.page-intro strong { font-size: .3rem; }
.page-intro small, .toolbar p, .hint { font-size: .22rem; }
.toolbar { padding: .2rem .28rem; }
.toolbar button, .save { height: .76rem; border-radius: .12rem; font-size: .27rem; }
.item { margin: 0 .28rem .18rem; padding: .28rem; border-radius: .2rem; }
.item-title { font-size: .29rem; }
.item p, .item pre { font-size: .22rem; }
.item pre { max-height: 3.2rem; border-radius: .1rem; }
.controls { margin-top: .18rem; }
.section-heading { font-size: .3rem; }
.control-item strong { font-size: .25rem; }
.control-item select { width: 3.2rem; height: .68rem; max-width: 52%; border-radius: .12rem; font-size: .22rem; }
.editor { border-radius: .22rem .22rem 0 0 !important; }

.governance-page > header { margin: 0; width: 100%; border-radius: 0; }
.governance-page .page-intro { margin: .16rem .24rem; padding: .2rem .22rem; background: #eef0f5; border: 0; border-radius: .12rem; box-shadow: none; }
.governance-page .intro-icon { background: #fff1f1; color: #c9232c; }
.governance-page /deep/ .van-tabs { margin: 0 .24rem; background: transparent; }
.governance-page /deep/ .van-tabs__wrap { height: .88rem; background: #fff; border-radius: .12rem; box-shadow: 0 .04rem .16rem rgba(30,35,60,.05); }
.governance-page /deep/ .van-tab { color: #73778a; font-size: .27rem; }
.governance-page /deep/ .van-tab--active { color: #303344; font-weight: 600; }
.governance-page /deep/ .van-tabs__line { width: .56rem; height: .05rem; background: #c9232c; }
.governance-page .toolbar { margin: .14rem 0 .16rem; padding: .18rem .2rem; background: #eef0f5; border-radius: .12rem; box-shadow: none; }
.governance-page .item { margin: 0 0 .14rem; padding: .22rem; border-radius: .14rem; box-shadow: 0 .04rem .16rem rgba(30,35,60,.05); }
.governance-page .controls { margin: .18rem .24rem; padding: .22rem; border-radius: .16rem; background: #fff; box-shadow: 0 .05rem .2rem rgba(30,35,60,.06); }
.governance-page .item pre { color: #5f6678; }
.governance-page /deep/ .van-empty { padding: .42rem 0 .5rem; }
.governance-page /deep/ .van-empty__image { width: 1.45rem; height: 1.1rem; }
.governance-page /deep/ .van-empty__description { margin-top: .1rem; color: #85899c; font-size: .24rem; }

@media (max-width: 360px) {
  .control-item {
    align-items: flex-start;
    gap: 8px;
  }
  .control-item select {
    width: 132px;
    min-width: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/boss/GovernanceCenter.vue